"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog"
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"
import { X, Copy, Facebook, Twitter, Linkedin, Mail, MessageCircle, Share2 } from "lucide-react"
import { toast } from 'sonner'
import { useIsMobile } from "@/hooks/use-mobile"
import { FaWhatsapp, FaTelegram } from "react-icons/fa"

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  url: string
}

export function ShareDialog({ open, onOpenChange, title, url }: ShareDialogProps) {
  const isMobile = useIsMobile()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) return null

  const shareUrl = url

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl)
    toast("Link copied", {
      description: "The link has been copied to your clipboard",
    })
  }

  const shareOptions = [
    {
      name: "Facebook",
      icon: <Facebook className="h-6 w-6" />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "WhatsApp",
      icon: <FaWhatsapp className="h-6 w-6" />,
      url: `https://wa.me/?text=${encodeURIComponent(`Check out this ${title}: ${shareUrl}`)}`,
    },
    {
      name: "Twitter",
      icon: <Twitter className="h-6 w-6" />,
      url: `https://x.com/intent/tweet?text=Check out this ${title}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "Email",
      icon: <Mail className="h-6 w-6" />,
      url: `mailto:?subject=Check out this ${title}&body=I thought you might be interested in this: ${shareUrl}`,
    },
    {
      name: "LinkedIn",
      icon: <Linkedin className="h-6 w-6" />,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      name: "Copy Link",
      icon: <Copy className="h-6 w-6" />,
      onClick: handleCopyLink,
    },
    {
      name: "Telegram",
      icon: <FaTelegram className="h-6 w-6" />,
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out this ${title}`)}`,
    },
    {
      name: "SMS",
      icon: <MessageCircle className="h-6 w-6" />,
      url: `sms:?body=${encodeURIComponent(`Check out this ${title}: ${shareUrl}`)}`,
    }
  ]

  const content = (
    <div className="relative rounded-lg overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
      
      <div className="p-4 bg-background/95 backdrop-blur-sm border-b border-primary/20 relative z-10">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">Share this vehicle</h2>
          {isMobile ? (
            <DrawerClose className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-primary/10 transition-colors">
              <X className="h-5 w-5" />
            </DrawerClose>
          ) : (
            <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-primary/10 transition-colors">
              <X className="h-5 w-5" />
            </DialogClose>
          )}
        </div>
      </div>

      <div className="p-6 w-full relative z-10 bg-background/95 backdrop-blur-sm">
        <div className="flex flex-col sm:flex-row border border-primary/20 rounded-lg overflow-hidden mb-6 hover:border-primary/50 transition-colors focus-within:neon-glow">
          <div className="flex-1 p-4 bg-background/50">
            <p className="text-sm text-muted-foreground">Your unique link</p>
            <p className="text-sm font-medium truncate">{shareUrl}</p>
          </div>
          <Button
            variant="ghost"
            className="h-12 sm:h-auto px-4 rounded-none sm:border-l border-t sm:border-t-0 border-primary/20 text-primary hover:text-primary hover:bg-primary/10"
            onClick={handleCopyLink}
          >
            <Copy className="h-5 w-5 mr-2" />
            Copy link
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {shareOptions.map((option) => (
            <a
              key={option.name}
              href={option.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (option.onClick) {
                  e.preventDefault()
                  option.onClick()
                }
              }}
              className="flex flex-col items-center group"
            >
              <div className="relative rounded-full h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center bg-primary/5 mb-3 group-hover:bg-primary/10 transition-colors border border-primary/20 group-hover:border-primary/40">
                <div className="text-primary">{option.icon}</div>
                <div className="absolute inset-0 rounded-full neon-glow opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
              <span className="text-sm font-medium text-center">{option.name}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90%]">{content}</DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-0 p-0 overflow-hidden">
        {content}
      </DialogContent>
    </Dialog>
  )
}

